import { useState, useEffect } from 'react'
import { 
  DollarSign, 
  ShoppingBag, 
  Clock, 
  XCircle, 
  TrendingUp,
  Users,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '@/components/ui/Modal'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs'
import { StatCard } from '@/components/misc/StatCard'
import { Breadcrumbs } from '@/components/misc/Breadcrumbs'
import { getOrderStats, getOrders } from '@/services/mock/orders'
import { formatCurrency, formatNumber, formatDate } from '@/utils/formatters'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts'
import { useNavigate } from 'react-router-dom'

// Mock chart data
const chartData = [
  { name: 'Mon', thisWeek: 4000, lastWeek: 2400 },
  { name: 'Tue', thisWeek: 3000, lastWeek: 1398 },
  { name: 'Wed', thisWeek: 2000, lastWeek: 9800 },
  { name: 'Thu', thisWeek: 2780, lastWeek: 3908 },
  { name: 'Fri', thisWeek: 1890, lastWeek: 4800 },
  { name: 'Sat', thisWeek: 2390, lastWeek: 3800 },
  { name: 'Sun', thisWeek: 3490, lastWeek: 4300 },
]

const userActivityData = [
  { name: '0-6', users: 20 },
  { name: '6-12', users: 180 },
  { name: '12-18', users: 320 },
  { name: '18-24', users: 250 },
]

const recentTransactions = [
  {
    id: '1',
    orderNumber: 'ORD-001',
    customer: 'John Doe',
    amount: 125.50,
    status: 'completed',
    date: new Date('2024-01-23T10:30:00Z'),
  },
  {
    id: '2',
    orderNumber: 'ORD-002',
    customer: 'Jane Smith',
    amount: 89.99,
    status: 'pending',
    date: new Date('2024-01-23T09:15:00Z'),
  },
  {
    id: '3',
    orderNumber: 'ORD-003',
    customer: 'Bob Johnson',
    amount: 199.99,
    status: 'completed',
    date: new Date('2024-01-23T08:45:00Z'),
  },
  {
    id: '4',
    orderNumber: 'ORD-004',
    customer: 'Alice Brown',
    amount: 65.75,
    status: 'failed',
    date: new Date('2024-01-23T07:20:00Z'),
  },
]

const bestSellingProducts = [
  {
    id: '1',
    name: 'Premium Cotton T-Shirt',
    sold: 145,
    revenue: 4335.50,
    image: '/api/placeholder/40/40',
  },
  {
    id: '2',
    name: 'Designer Jeans',
    sold: 89,
    revenue: 8009.11,
    image: '/api/placeholder/40/40',
  },
  {
    id: '3',
    name: 'Wireless Headphones',
    sold: 67,
    revenue: 13399.33,
    image: '/api/placeholder/40/40',
  },
  {
    id: '4',
    name: 'Sport Sneakers',
    sold: 54,
    revenue: 5399.46,
    image: '/api/placeholder/40/40',
  },
]

export default function DashboardPage() {
  const navigate = useNavigate()
  const [stats, setStats] = useState(null)
  const [exportOpen, setExportOpen] = useState(false)
  const [timeframe, setTimeframe] = useState('7') // default 7 days
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [format, setFormat] = useState('csv')
  const [exporting, setExporting] = useState(false)
  const [activeTab, setActiveTab] = useState('thisWeek')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await getOrderStats()
        setStats(data)
      } catch (error) {
        console.error('Failed to fetch stats:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-48 mb-6" />
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-muted rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <Breadcrumbs />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setExportOpen(true)}>
            Export
          </Button>
          <Button size="sm" onClick={() => navigate('/products/add')}>
            Add Product
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Sales"
          value={stats?.totalSales || 0}
          change={12.5}
          format="currency"
          changeType="percentage"
          icon={DollarSign}
        />
        <StatCard
          title="Total Orders"
          value={stats?.totalOrders || 0}
          change={8.2}
          format="number"
          changeType="percentage"
          icon={ShoppingBag}
        />
        <StatCard
          title="Pending Orders"
          value={stats?.pendingOrders || 0}
          change={-2.1}
          format="number"
          changeType="percentage"
          icon={Clock}
        />
        <StatCard
          title="Canceled Orders"
          value={stats?.canceledOrders || 0}
          change={-5.3}
          format="number"
          changeType="percentage"
          icon={XCircle}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Sales Chart */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Sales Overview</CardTitle>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList>
                    <TabsTrigger value="thisWeek">This Week</TabsTrigger>
                    <TabsTrigger value="lastWeek">Last Week</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), '']}
                    labelStyle={{ color: 'var(--foreground)' }}
                    contentStyle={{ 
                      backgroundColor: 'var(--popover)',
                      border: '1px solid var(--border)',
                      borderRadius: '8px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey={activeTab} 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--primary))', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* User Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Users Last 30 Minutes</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={userActivityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip 
                  formatter={(value) => [value, 'Users']}
                  labelStyle={{ color: 'var(--foreground)' }}
                  contentStyle={{ 
                    backgroundColor: 'var(--popover)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="users" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Total Users</span>
                <span className="font-medium">770</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Active Now</span>
                <span className="font-medium text-success-600">145</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Sales by State */}
        <Card>
          <CardHeader>
            <CardTitle>Sales by State</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.salesByState?.map((region, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{region.flag}</span>
                    <div>
                      <p className="font-medium">{region.state}</p>
                      <p className="text-sm text-muted-foreground">
                        {region.percentage}% of total sales
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{formatCurrency(region.sales)}</p>
                    <div className="flex items-center text-sm">
                      {region.percentage > 15 ? (
                        <ArrowUpRight className="h-3 w-3 text-success-600 mr-1" />
                      ) : (
                        <ArrowDownRight className="h-3 w-3 text-danger-600 mr-1" />
                      )}
                      <span className={region.percentage > 15 ? 'text-success-600' : 'text-danger-600'}>
                        {region.percentage}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Transactions */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Recent Transactions</CardTitle>
              <Button variant="ghost" size="sm">
                View All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentTransactions.map((transaction) => (
                <div key={transaction.id} className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{transaction.orderNumber}</p>
                    <p className="text-sm text-muted-foreground">{transaction.customer}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{formatCurrency(transaction.amount)}</p>
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant={
                          transaction.status === 'completed' ? 'success' :
                          transaction.status === 'pending' ? 'warning' : 'danger'
                        }
                        size="sm"
                      >
                        {transaction.status}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Best Selling Products */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Best Selling Products</CardTitle>
            <Button variant="ghost" size="sm">
              View All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {bestSellingProducts.map((product) => (
              <div key={product.id} className="flex items-center space-x-3 p-3 rounded-xl border hover:bg-accent transition-colors">
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="w-12 h-12 rounded-lg object-cover"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{product.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {product.sold} sold
                  </p>
                  <p className="text-sm font-medium text-success-600">
                    {formatCurrency(product.revenue)}
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-6 pt-4 border-t">
            <div className="flex items-center justify-center">
              <Button variant="outline" className="w-full" onClick={() => navigate('/products/add')}>
                <Users className="h-4 w-4 mr-2" />
                Add New Product
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Export Modal */}
      <Modal open={exportOpen} onOpenChange={setExportOpen}>
        <ModalHeader onClose={() => setExportOpen(false)}>
          <ModalTitle>Export Orders</ModalTitle>
        </ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            <div>
              <p className="font-medium">Select timeframe</p>
              <div className="flex items-center gap-3 mt-2">
                <label className="flex items-center gap-2">
                  <input type="radio" name="tf" value="1" checked={timeframe==='1'} onChange={() => setTimeframe('1')} />
                  <span>1 day</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" name="tf" value="7" checked={timeframe==='7'} onChange={() => setTimeframe('7')} />
                  <span>7 days</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" name="tf" value="15" checked={timeframe==='15'} onChange={() => setTimeframe('15')} />
                  <span>15 days</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" name="tf" value="custom" checked={timeframe==='custom'} onChange={() => setTimeframe('custom')} />
                  <span>Custom</span>
                </label>
              </div>
              {timeframe === 'custom' && (
                <div className="flex gap-2 mt-2">
                  <input type="date" className="input" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
                  <input type="date" className="input" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
                </div>
              )}
            </div>

            <div>
              <p className="font-medium">Export format</p>
              <div className="flex items-center gap-4 mt-2">
                <label className="flex items-center gap-2">
                  <input type="radio" name="fmt" value="csv" checked={format==='csv'} onChange={() => setFormat('csv')} />
                  <span>CSV</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" name="fmt" value="pdf" checked={format==='pdf'} onChange={() => setFormat('pdf')} />
                  <span>PDF</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" name="fmt" value="xlsx" checked={format==='xlsx'} onChange={() => setFormat('xlsx')} />
                  <span>XLSX</span>
                </label>
              </div>
            </div>

            <p className="text-sm text-muted-foreground">The export will include full order details (items, totals, shipping, payment, timestamps).</p>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setExportOpen(false)}>Cancel</Button>
          <Button onClick={async () => {
            setExporting(true)
            try {
              // fetch all orders and filter by selected timeframe
              const res = await getOrders({ size: 1000 })
              const all = res.data || []
              let fromDate = null
              let toDate = new Date()
              if (timeframe === 'custom' && customFrom && customTo) {
                fromDate = new Date(customFrom)
                toDate = new Date(customTo)
                // include end of day
                toDate.setHours(23,59,59,999)
              } else {
                const days = Number(timeframe)
                fromDate = new Date()
                fromDate.setDate(fromDate.getDate() - (isNaN(days) ? 7 : days))
              }

              const filtered = all.filter(o => {
                const d = new Date(o.createdAt)
                return d >= fromDate && d <= toDate
              })

              // build rows
              const rows = filtered.map(o => ({
                orderNumber: o.orderNumber,
                customerName: o.customerName,
                customerEmail: o.customerEmail,
                customerPhone: o.customerPhone || '',
                items: o.items.map(i => `${i.productName} x${i.quantity}`).join(' | '),
                subtotal: o.subtotal,
                tax: o.tax,
                shipping: o.shipping,
                total: o.total,
                status: o.status,
                paymentMethod: o.paymentMethod,
                shippingAddress: `${o.shippingAddress.street}, ${o.shippingAddress.city}, ${o.shippingAddress.state}, ${o.shippingAddress.zipCode}, ${o.shippingAddress.country}`,
                createdAt: o.createdAt,
                updatedAt: o.updatedAt || '',
                deliveredAt: o.deliveredAt || '',
              }))

              const filename = `orders_${new Date().toISOString().slice(0,10)}`

              if (format === 'csv') {
                const header = Object.keys(rows[0] || {
                  orderNumber: '', customerName: '', customerEmail: '', customerPhone: '', items: '', subtotal: '', tax: '', shipping: '', total: '', status: '', paymentMethod: '', shippingAddress: '', createdAt: '', updatedAt: '', deliveredAt: ''
                })
                const csv = [header.join(',')].concat(rows.map(r => header.map(h => `"${String(r[h] ?? '').replace(/"/g,'""')}"`).join(','))).join('\n')
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `${filename}.csv`
                document.body.appendChild(a)
                a.click()
                a.remove()
                URL.revokeObjectURL(url)
              } else if (format === 'xlsx') {
                try {
                  const XLSX = await import('xlsx')
                  const ws = XLSX.utils.json_to_sheet(rows)
                  const wb = XLSX.utils.book_new()
                  XLSX.utils.book_append_sheet(wb, ws, 'Orders')
                  XLSX.writeFile(wb, `${filename}.xlsx`)
                } catch (err) {
                  console.error('XLSX export failed', err)
                  alert('XLSX export requires the "xlsx" package. Install it with "npm i xlsx" or choose CSV/PDF.')
                }
              } else if (format === 'pdf') {
                // Open a new window with a table and trigger print (user can save as PDF)
                const popup = window.open('', '_blank')
                const tableHtml = `
                  <html>
                    <head>
                      <title>Orders Export</title>
                      <style>table{width:100%;border-collapse:collapse}td,th{border:1px solid #ccc;padding:6px;font-size:12px}</style>
                    </head>
                    <body>
                      <h2>Orders Export</h2>
                      <table>
                        <thead>
                          <tr>${Object.keys(rows[0] || {}).map(h => `<th>${h}</th>`).join('')}</tr>
                        </thead>
                        <tbody>
                          ${rows.map(r => `<tr>${Object.keys(r).map(k => `<td>${String(r[k] ?? '')}</td>`).join('')}</tr>`).join('')}
                        </tbody>
                      </table>
                    </body>
                  </html>
                `
                popup.document.write(tableHtml)
                popup.document.close()
                // give it a moment to render
                setTimeout(() => { popup.print() }, 500)
              }

              setExportOpen(false)
            } catch (err) {
              console.error('Export failed', err)
              alert('Export failed. Check console for details.')
            } finally {
              setExporting(false)
            }
          }} loading={exporting}>
            Export
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}